import React from 'react';
import { INPUT_BASE, INPUT_PADDING, INPUT_STATE } from '@/shared/components/form/input/inputStyles';
import { useAddOnGroupsQuery } from '../hooks/useAddOnGroupsQuery';
import type { AddOnGroupRecord } from '../services/addonGroup.types';

interface Props {
    value: AddOnGroupRecord | null;
    onChange: (next: AddOnGroupRecord | null) => void;
    excludeIds?: string[];
    disabled?: boolean;
    id?: string;
    error?: string | null;
}

function useDebounce<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = React.useState(value);
    React.useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

export function AddOnGroupCombobox({
    value,
    onChange,
    excludeIds = [],
    disabled = false,
    id,
    error,
}: Props) {
    const [search, setSearch]       = React.useState('');
    const [open, setOpen]           = React.useState(false);
    const [highlighted, setHighlighted] = React.useState(0);
    const debouncedSearch = useDebounce(search, 300);

    React.useEffect(() => {
        setHighlighted(0);
    }, [debouncedSearch]);

    const { data, isFetching } = useAddOnGroupsQuery({
        search: debouncedSearch,
        status: 'active',
        per_page: 20,
    });
    const options = (data?.data ?? []).filter((o) => !excludeIds.includes(o.id));

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                if (e.key === 'Enter') e.preventDefault();
                setOpen(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, options.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (options[highlighted]) {
                onChange(options[highlighted]);
                setOpen(false);
                setSearch('');
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className="relative">
            <input
                id={id}
                type="text"
                className={`${INPUT_BASE} ${INPUT_PADDING} ${disabled ? INPUT_STATE.disabled : error ? INPUT_STATE.error : INPUT_STATE.default}`}
                placeholder={open || !value ? 'Search active optional extra groups…' : undefined}
                value={open ? search : (value ? value.name : '')}
                disabled={disabled}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
            />
            {error && <p className="mt-1 text-xs text-mp-error">{error}</p>}
            {open && (
                <ul
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-mp-border bg-mp-surface text-mp-foreground shadow"
                    role="listbox"
                >
                    {isFetching && (
                        <li className="px-3 py-2 text-sm text-mp-muted">Loading…</li>
                    )}
                    {!isFetching && options.length === 0 && (
                        <li className="px-3 py-2 text-sm text-mp-muted">
                            No active optional extra groups match.
                        </li>
                    )}
                    {options.map((opt, idx) => (
                        <li
                            key={opt.id}
                            role="option"
                            aria-selected={highlighted === idx}
                            className={`px-3 py-2 cursor-pointer text-sm ${highlighted === idx ? 'bg-mp-primary-content text-mp-link' : 'hover:bg-mp-background'}`}
                            onMouseDown={() => {
                                onChange(opt);
                                setOpen(false);
                                setSearch('');
                            }}
                            onMouseEnter={() => setHighlighted(idx)}
                        >
                            {opt.name}
                            {typeof opt.addons_count === 'number' && (
                                <span className="ml-1 text-xs text-mp-muted">
                                    ({opt.addons_count} optional extras)
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
